import {GeneratedType} from "@shareledgerjs/signing";
import Long from "long";

import {BaseClient} from "../../baseclient";
import {MsgExecuteContractEncodeObject, MsgInstantiateContractEncodeObject, WasmTxExtension} from "../wasm";
import {Cw1155RoyaltiesExecuteMsg, InstantiateMsg as Cw1155BaseInstantiateMsg} from "./types/Cw1155Royalties.types";
import {
  Cw2981RoyaltiesAutoincrementExecuteMsg,
  InstantiateMsg as Cw2981RoyaltiesAutoincrementInstantiateMsg,
  Metadata as Cw2981RoyaltiesAutoincrementMetadata
} from "./types/Cw2981RoyaltiesAutoincrement.types";

export function createNftTypes(): ReadonlyArray<[string, GeneratedType]> {
  return [];
}

export interface NftTxExtensionMethods {
  instantiate(
    owner: string,
    codeId: Long,
    label: string,
    msg: Cw2981RoyaltiesAutoincrementInstantiateMsg | Cw1155BaseInstantiateMsg
  ): MsgInstantiateContractEncodeObject;
  mint721(
    contract: string,
    owner: string,
    recipient: string,
    tokenUri: string,
    extension?: Cw2981RoyaltiesAutoincrementMetadata
  ): MsgExecuteContractEncodeObject;
  mint1155(
    contract: string,
    owner: string,
    recipient: string,
    tokenId: string,
    amount: string,
    msg?: string
  ): MsgExecuteContractEncodeObject;
  transfer(
    contract: string,
    owner: string,
    recipient: string,
    tokenId: string,
    amount?: string,
    msg?: string
  ): MsgExecuteContractEncodeObject;
  burn(contract: string, owner: string, tokenId: string, amount?: string): MsgExecuteContractEncodeObject;
}

export interface NftTxExtension {
  readonly nft: NftTxExtensionMethods;
}

export function NftTxExtension<T extends {new (...args: any[]): BaseClient & NftTxExtension & WasmTxExtension}>(constructor: T): T {
  return class extends constructor {
    override get nft() {
      return {
        ...super["nft"],
        instantiate: (
          owner: string,
          codeId: Long,
          label: string,
          msg: Cw1155BaseInstantiateMsg | Cw2981RoyaltiesAutoincrementInstantiateMsg
        ) => {
          return this.wasm.instantiateContract(owner, owner, codeId, label, msg, []);
        },
        mint721: (
          contract: string,
          owner: string,
          recipient: string,
          tokenUri: string,
          extension?: Cw2981RoyaltiesAutoincrementMetadata
        ) => {
          return this.wasm.executeContract<Cw2981RoyaltiesAutoincrementExecuteMsg>(
            owner,
            contract,
            {
              mint: {
                owner: recipient,
                extension: extension, // TODO
                token_uri: tokenUri
              }
            },
            []
          );
        },
        mint1155: (contract: string, owner: string, recipient: string, tokenId: string, amount: string, msg?: string) => {
          return this.wasm.executeContract<Cw1155RoyaltiesExecuteMsg>(
            owner,
            contract,
            {
              mint: {
                to: recipient,
                token_id: tokenId,
                value: amount,
                msg
              }
            },
            []
          );
        },
        transfer: (contract: string, owner: string, recipient: string, tokenId: string, amount?: string, msg?: string) => {
          if (amount) {
            return this.wasm.executeContract<Cw1155RoyaltiesExecuteMsg>(
              owner,
              contract,
              {
                send_from: {
                  from: owner,
                  to: recipient,
                  token_id: tokenId,
                  value: amount,
                  msg
                }
              },
              []
            );
          }
          return this.wasm.executeContract<Cw2981RoyaltiesAutoincrementExecuteMsg>(
            owner,
            contract,
            {
              transfer_nft: {
                recipient,
                token_id: tokenId
              }
            },
            []
          );
        },
        burn: (contract: string, owner: string, tokenId: string, amount?: string) => {
          if (amount) {
            return this.wasm.executeContract<Cw1155RoyaltiesExecuteMsg>(
              owner,
              contract,
              {
                burn: {
                  from: owner,
                  token_id: tokenId,
                  value: amount
                }
              },
              []
            );
          }
          return this.wasm.executeContract<Cw2981RoyaltiesAutoincrementExecuteMsg>(
            owner,
            contract,
            {
              burn: {
                token_id: tokenId
              }
            },
            []
          );
        }
      };
    }
  };
}
